import React, { useCallback, useRef } from 'react';
import jsqr from 'jsqr';

import PropTypes from 'prop-types';

import { convertBase64StringToImageData } from '../../utils/dom';
import Scanner from '../Scanner';

const QRScanner = ({
	onFindCode,
	intervalMs,
	duplicateScanTimeoutMs,
	...props
}) => {
	const onFindCodeRef = useRef();
	const previousQRTimeoutRef = useRef();
	const previousQrDataRef = useRef();

	onFindCodeRef.current = onFindCode;

	// Allows duplicate scans at a set timeout
	// Used to give user feedback (show error toast)
	const setPreviousQrData = useCallback(
		(qrData) => {
			// Clear previous timeout & set previous QR data
			window.clearInterval(previousQRTimeoutRef.current);
			previousQrDataRef.current = qrData;

			// Create a timeout to clear data after set time
			previousQRTimeoutRef.current = setTimeout(
				() => (previousQrDataRef.current = null),
				duplicateScanTimeoutMs
			);
		},
		[duplicateScanTimeoutMs]
	);

	const onScan = useCallback(
		async (img) => {
			const width = 640;
			const height = 480;

			const imageData = await convertBase64StringToImageData(
				img,
				width,
				height
			);

			const qr = jsqr(imageData.data, width, height, {
				inversionAttempts: 'dontInvert',
			});

			// Prevent on change being called repeatedly for same scan URL
			if (qr?.data && qr.data !== previousQrDataRef.current) {
				setPreviousQrData(qr.data);
				onFindCodeRef.current({ imageSrc: img, image: imageData, qr });
			}
		},
		[setPreviousQrData]
	);

	return <Scanner onScan={onScan} intervalMs={intervalMs} {...props} />;
};

QRScanner.propTypes = {
	onFindCode: PropTypes.func.isRequired,
	isScanning: PropTypes.bool,
	isPaused: PropTypes.bool,
	intervalMs: PropTypes.number,
	duplicateScanTimeoutMs: PropTypes.number,
};

QRScanner.defaultProps = {
	intervalMs: 250,
	isScanning: true,
	isPaused: false,
	duplicateScanTimeoutMs: 2000,
};

export default QRScanner;
